import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Feather, ArrowRight } from "lucide-react";
import { API, api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { HeartButton } from "./HeartButton";

export const ShareMomentForm = ({ onDone, milestone }) => {
  const { user } = useAuth();
  const [name, setName] = useState((user?.name || "").split(" ")[0]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/stories", { display_name: name.trim(), text: text.trim(), milestone: milestone || null });
      setSent(true);
      setText("");
      onDone?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail?.toString() || "Could not send. Please try again.");
    }
    setSending(false);
  };

  if (sent) return (
    <div className="rounded-xl border border-amber-400/30 bg-amber-500/5 p-4 text-sm text-amber-100" data-testid="moment-sent">
      Thank you for sharing. Gabi reads every one, and yours may be featured on the homepage.
      <button type="button" onClick={() => setSent(false)} className="block mt-2 text-xs text-amber-300 hover:text-amber-200" data-testid="moment-another">Share another</button>
    </div>
  );

  return (
    <form onSubmit={submit} className="space-y-3" data-testid="moment-form">
      <textarea required minLength={20} maxLength={600} rows={4} value={text} onChange={(e) => setText(e.target.value)}
        aria-label="Your recovery moment" placeholder="A moment that mattered: the day something shifted, a small win, what kept you going…"
        data-testid="moment-text" className="wwr-input w-full rounded-xl px-4 py-3 text-sm" />
      <div className="flex flex-col sm:flex-row gap-3">
        <input required maxLength={60} value={name} onChange={(e) => setName(e.target.value)} aria-label="Name to show"
          placeholder="Name to show (first name or initials)" data-testid="moment-name" className="wwr-input flex-1 rounded-xl px-4 py-3 text-sm" />
        <button type="submit" disabled={sending} data-testid="moment-submit" className="btn-primary px-6 py-3 text-sm disabled:opacity-60">
          {sending ? "Sending…" : "Share my moment"}
        </button>
      </div>
      <p className="text-[11px] text-neutral-500">{text.length}/600 · Only shown publicly if Gabi features it.</p>
    </form>
  );
};

export const RecoveryMoments = () => {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  useEffect(() => { axios.get(`${API}/stories`).then(({ data }) => setItems(data)).catch(() => {}); }, []);

  return (
    <div className="mt-10" data-testid="recovery-moments">
      <div className="text-center mb-6">
        <div className="text-xs uppercase tracking-[0.28em] text-amber-300/80 mb-2 inline-flex items-center gap-2"><Feather size={13} /> Recovery moments</div>
        <p className="text-sm text-neutral-400">Small moments from our community. Every one of them counts.</p>
      </div>
      {items.length > 0 && (
        <div className="space-y-4 mb-8">
          {items.map((m) => (
            <figure key={m.id} className="card-surface p-5" data-testid={`moment-${m.id}`}>
              <blockquote className="text-sm text-neutral-200 leading-relaxed italic">"{m.text}"</blockquote>
              <figcaption className="flex items-center justify-between gap-3 mt-3">
                <span className="text-xs text-neutral-500">
                  — {m.display_name}
                  {m.milestone && <span className="ml-2 rounded-full bg-amber-400/15 text-amber-200 px-2 py-0.5 text-[10px]" data-testid={`moment-milestone-${m.id}`}>Day {m.milestone}</span>}
                </span>
                <HeartButton id={m.id} initial={m.hearts || 0} />
              </figcaption>
            </figure>
          ))}
        </div>
      )}
      <div className="text-center">
        {!user ? (
          <Link to="/signup" data-testid="moment-signup-cta" className="inline-flex items-center gap-2 rounded-full border border-amber-400/40 px-5 py-2.5 text-sm text-amber-100 hover:bg-amber-500/10">
            Join to share your moment <ArrowRight size={14} />
          </Link>
        ) : open ? (
          <div className="text-left"><ShareMomentForm /></div>
        ) : (
          <button type="button" onClick={() => setOpen(true)} data-testid="moment-open-form"
            className="inline-flex items-center gap-2 rounded-full border border-amber-400/40 px-5 py-2.5 text-sm text-amber-100 hover:bg-amber-500/10">
            Share your moment <ArrowRight size={14} />
          </button>
        )}
      </div>
    </div>
  );
};
